import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { LockIcon, MailIcon, AlertCircleIcon, ShieldCheckIcon } from "lucide-react";
import { safePostLoginRedirect } from "@/lib/auth-paths";

interface LocationState {
  from?: string;
}

type LoginStep = "credentials" | "otp";

export default function LoginPage() {
  const [step, setStep] = useState<LoginStep>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "", otp: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { login, verifyLogin2fa, resendLogin2faCode, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = safePostLoginRedirect((location.state as LocationState)?.from);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startResendCooldown = (seconds = 60) => {
    setResendCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  if (isAuthenticated) {
    return null;
  }

  const validateCredentials = (): boolean => {
    let isValid = true;
    const newErrors = { email: "", password: "", otp: "" };

    if (!email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
      isValid = false;
    }

    if (!password) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateOtp = (): boolean => {
    const trimmed = otpCode.trim();
    if (!trimmed || !/^\d{6}$/.test(trimmed)) {
      setErrors((prev) => ({ ...prev, otp: "Enter the 6-digit code from your email" }));
      return false;
    }
    setErrors((prev) => ({ ...prev, otp: "" }));
    return true;
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateCredentials()) return;

    setIsLoading(true);
    setLoginError("");

    try {
      const result = await login(email, password);

      if (result.status === "requires2fa") {
        setChallengeId(result.challengeId);
        setMaskedEmail(result.maskedEmail);
        setStep("otp");
        setOtpCode("");
        startResendCooldown(60);
        return;
      }

      if (result.status === "success") {
        navigate(from, { replace: true });
        return;
      }

      setLoginError("Login failed. Please check your credentials and try again.");
    } catch (error: unknown) {
      const errorMessage =
        (error as { message?: string })?.message || "An error occurred. Please try again later.";
      setLoginError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateOtp()) return;

    setIsLoading(true);
    setLoginError("");

    try {
      const success = await verifyLogin2fa(challengeId, otpCode.trim());
      if (success) {
        navigate(from, { replace: true });
      }
    } catch (error: unknown) {
      const errorMessage =
        (error as { message?: string })?.message || "Verification failed. Please try again.";
      setLoginError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!challengeId || resendCooldown > 0 || isResending) return;

    setIsResending(true);
    setLoginError("");

    try {
      const result = await resendLogin2faCode(challengeId);
      if (result) {
        setChallengeId(result.challengeId);
        setMaskedEmail(result.maskedEmail);
        startResendCooldown(60);
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setOtpCode("");
    setChallengeId("");
    setMaskedEmail("");
    setLoginError("");
    setErrors({ email: "", password: "", otp: "" });
  };

  return (
    <div className="login-shell min-h-screen flex items-center justify-center p-4 sm:p-6">
      <div className="login-panel w-full max-w-md space-y-8 rounded-2xl p-8 sm:p-10">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl brand-mark text-xl font-bold tracking-wide">
            ACTS
          </div>
          <h1 className="mt-5 text-2xl font-bold tracking-wide text-foreground sm:text-[1.65rem]">
            Americation Career & Training School
          </h1>
          <p className="mt-1 text-sm font-medium text-primary">Admin Portal</p>
          <p className="mt-3 text-sm text-muted-foreground">
            {step === "credentials"
              ? "Sign in to access your admin dashboard"
              : "Enter the verification code sent to your email"}
          </p>
        </div>

        <Separator className="bg-border/80" />

        {step === "credentials" ? (
          <form onSubmit={handleCredentialsSubmit} className="space-y-6">
            {loginError && (
              <div className="p-3 text-sm bg-destructive/10 border border-destructive/30 rounded-lg text-destructive flex items-start">
                <AlertCircleIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <p>{loginError}</p>
              </div>
            )}

            <div className="space-y-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                  <MailIcon className="h-5 w-5" />
                </div>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-10 h-11 bg-background/70 ${errors.email ? "border-destructive focus:ring-destructive" : ""}`}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <div className="text-xs flex items-center text-destructive">
                  <AlertCircleIcon className="h-3 w-3 mr-1" />
                  {errors.email}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                  <LockIcon className="h-5 w-5" />
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 h-11 bg-background/70 ${errors.password ? "border-destructive focus:ring-destructive" : ""}`}
                  autoComplete="current-password"
                />
              </div>
              {errors.password && (
                <div className="text-xs flex items-center text-destructive">
                  <AlertCircleIcon className="h-3 w-3 mr-1" />
                  {errors.password}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full h-11 text-sm font-semibold" disabled={isLoading}>
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                "Continue"
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="space-y-6">
            {loginError && (
              <div className="p-3 text-sm bg-destructive/10 border border-destructive/30 rounded-lg text-destructive flex items-start">
                <AlertCircleIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <p>{loginError}</p>
              </div>
            )}

            <div className="rounded-xl border border-primary/15 bg-accent/50 p-4 text-sm text-muted-foreground flex items-start gap-3">
              <ShieldCheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <p>
                We sent a 6-digit code to <span className="font-medium text-foreground">{maskedEmail}</span>.
                It expires in 15 minutes.
              </p>
            </div>

            <div className="space-y-2">
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className={`text-center text-2xl tracking-[0.4em] font-mono h-14 bg-background/70 ${errors.otp ? "border-destructive" : ""}`}
                autoComplete="one-time-code"
                autoFocus
              />
              {errors.otp && (
                <div className="text-xs flex items-center justify-center text-destructive">
                  <AlertCircleIcon className="h-3 w-3 mr-1" />
                  {errors.otp}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full h-11 text-sm font-semibold" disabled={isLoading}>
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                "Verify and sign in"
              )}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleBackToCredentials}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Back to sign in
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || isResending}
                className="text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
              >
                {isResending
                  ? "Sending..."
                  : resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend code"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
